import { Popover, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { WarningTriangleIcon } from '@vizit/icons';
import { colors } from '@vizit/theme';
import { plural } from '@vizit/utils';
import * as React from 'react';
import { Text as AriaText, OverlayTriggerStateContext, PopoverContext, Provider } from 'react-aria-components';
import type { BenchmarkOption } from '../benchmark-select-field';

/** How many images a custom set still needs to score against, 0 once it has enough. */
function imagesNeeded(option: BenchmarkOption) {
  return Math.max((option.minimumImages ?? 0) - (option.imageCount ?? 0), 0);
}

function underMinimumWarning(option: BenchmarkOption) {
  return `Needs ${plural(imagesNeeded(option), 'more image')} in order to compose a meaningful benchmark`;
}

interface BenchmarkRowPopoutProps {
  option: BenchmarkOption;
  /** Whether the row is hovered or keyboard-focused */
  isActive: boolean;
}

/**
 * A custom benchmark row's hover card, shown beside the row while it is hovered or focused: the set's name, its
 * image count, its description and, while it is under its minimum, how many images it still needs. Non-interactive,
 * it never takes the pointer or focus. The row itself carries the same facts as its description for assistive tech.
 *
 * Disabled rows get no hover or focus state from the menu, so the pointer is also tracked on the row directly. The
 * card is placed against an anchor that covers the row, so it follows the row.
 */
function BenchmarkRowPopout({ option, isActive }: BenchmarkRowPopoutProps) {
  const anchorRef = React.useRef<HTMLSpanElement>(null);
  const [isPointerOver, setIsPointerOver] = React.useState(false);

  // The anchor covers the row; its parent is the row, which reports the pointer even while disabled.
  React.useEffect(() => {
    const row = anchorRef.current?.parentElement;
    if (!row) {
      return;
    }
    const enter = () => setIsPointerOver(true);
    const leave = () => setIsPointerOver(false);
    row.addEventListener('pointerenter', enter);
    row.addEventListener('pointerleave', leave);
    return () => {
      row.removeEventListener('pointerenter', enter);
      row.removeEventListener('pointerleave', leave);
    };
  }, []);
  const isOpen = isActive || isPointerOver;
  const needed = imagesNeeded(option);
  const imageCount = plural(option.imageCount ?? 0, 'image');

  return (
    <>
      <span ref={anchorRef} aria-hidden className="pointer-events-none absolute inset-0" />
      <AriaText slot="description" className="sr-only">
        {[imageCount, option.description, needed > 0 ? underMinimumWarning(option) : null].filter(Boolean).join('. ')}
      </AriaText>
      {/* Inside the menu, a popover would take the menu trigger's state and stay open with the menu. */}
      <Provider
        values={[
          [PopoverContext, null],
          [OverlayTriggerStateContext, null],
        ]}
      >
        <Popover
          triggerRef={anchorRef}
          isOpen={isOpen}
          isNonModal
          placement="end top"
          offset={12}
          className="pointer-events-none w-[280px] p-6 shadow-lg"
        >
          <div className="flex flex-col gap-3" aria-hidden>
            <Text size={TextSize.Sm} weight={TextWeight.Semibold} color={TextColor.Primary} showEllipsis={false}>
              {option.name}
            </Text>
            <Text
              size={TextSize.Xs}
              weight={TextWeight.Medium}
              color={TextColor.Tertiary}
              className="font-mono uppercase"
            >
              {imageCount}
            </Text>
            {option.description && (
              <Text size={TextSize.Sm} color={TextColor.Secondary} showEllipsis={false}>
                {option.description}
              </Text>
            )}
            {needed > 0 && (
              <div className="flex items-start gap-3 border border-warning-200 bg-warning-50 p-4">
                <WarningTriangleIcon size={14} color={colors.warning['500']} className="mt-1 shrink-0" />
                <Text size={TextSize.Xs} className="text-warning-700" showEllipsis={false}>
                  {underMinimumWarning(option)}
                </Text>
              </div>
            )}
          </div>
        </Popover>
      </Provider>
    </>
  );
}

export { BenchmarkRowPopout };
